import { useQuery } from '@apollo/client';

import EstimatedReadTimeDataQuery from 'bundles/unified-common/queries/EstimatedReadTimeData.graphql';
import type {
  EstimatedReadTimeDataQuery as EstimatedReadTimeDataQueryType,
  EstimatedReadTimeDataQueryVariables,
} from 'bundles/unified-common/queries/__generated__/EstimatedReadTimeData';

type EstimatedReadTime = {
  [key: string]: {
    id: string;
    estimatedReadTime?: number;
    estimatedReadTimeLabel?: string;
  };
};

type MultiEstimatedReadTime = {
  loading: boolean;
  results: EstimatedReadTime | undefined;
};

/**
 * @description Fetches estimated read time data for a list of article slugs.
 */
export const useEstimatedReadTimesData = ({ slugs }: { slugs: string[] }): MultiEstimatedReadTime => {
  // Nostos queries accept a list of parameters as a string delimited by commas.
  // Contentful slugs should be alphanumerics and dashes.
  const slug = slugs.join(',');

  const { data, loading, error } = useQuery<EstimatedReadTimeDataQueryType, EstimatedReadTimeDataQueryVariables>(
    EstimatedReadTimeDataQuery,
    {
      skip: !slug,
      variables: { slug },
      errorPolicy: 'all',
      ssr: false,
      context: { clientName: 'gatewayGql' },
    }
  );

  if (error) {
    return {
      loading: false,
      results: undefined,
    };
  }

  const results: EstimatedReadTime = {};
  data?.NaptimeQueries?.ExternallyAccessibleNostosV1Resource?.getAllProperties?.elements?.forEach((val) => {
    if (!val?.id) return;

    results[val?.id] = {
      id: val?.id,
      estimatedReadTime: val?.content?.estimatedreadingtime,
      estimatedReadTimeLabel: val?.content?.estimatedreadingtimelabel,
    };
  });

  return {
    loading,
    results,
  };
};
